import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";

const navItems = [
  { label: "首页", to: "/", end: true, icon: "ri-home-5-line" },
  { label: "电影", to: "/category/movie", icon: "ri-film-line" },
  { label: "短片", to: "/category/short", icon: "ri-movie-2-line" },
  { label: "热门", to: "/category/hot", icon: "ri-fire-line" },
  { label: "关于", to: "/about", icon: "ri-information-line" },
];

export default function SiteHeader() {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
    if (location.pathname === "/search") {
      const params = new URLSearchParams(location.search);
      setTerm(params.get("q") ?? "");
    } else {
      setTerm("");
    }
  }, [location.pathname, location.search]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const keyword = term.trim();
    setOpen(false);
    navigate(keyword ? `/search?q=${encodeURIComponent(keyword)}` : "/search");
  };

  return (
    <header className="sticky top-0 z-50 border-b border-background-200/80 bg-background-50/95 backdrop-blur-md">
      <div className="w-full px-4 md:px-6 lg:px-8">
        <div className="flex h-16 items-center gap-3 md:h-[72px] md:gap-6">
          <Link to="/" className="focus-ring group flex shrink-0 items-center gap-2.5 rounded-md">
            <span className="relative flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50 md:h-10 md:w-10">
              <span className="font-heading text-base font-semibold md:text-lg">色</span>
              <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-background-50 bg-accent-500" />
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading text-base font-semibold tracking-tight text-foreground-950 md:text-lg">
                色色视频
              </span>
              <span className="mt-1 hidden font-label text-[9px] uppercase tracking-[0.28em] text-foreground-400 sm:block">
                SESE VIDEO
              </span>
            </span>
          </Link>

          <nav className="hidden items-center gap-1 md:flex" aria-label="主导航">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `focus-ring relative whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                    isActive
                      ? "text-primary-700"
                      : "text-foreground-600 hover:bg-background-100 hover:text-foreground-950"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {item.label}
                    <span
                      className={`absolute inset-x-3 -bottom-[1px] h-[2px] rounded-full bg-accent-500 transition-opacity ${
                        isActive ? "opacity-100" : "opacity-0"
                      }`}
                      aria-hidden="true"
                    />
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <form
            onSubmit={handleSubmit}
            className="ml-auto hidden w-full max-w-[240px] items-center gap-2 rounded-full border border-background-300/80 bg-white px-3 py-1.5 transition-colors focus-within:border-secondary-400 md:flex lg:max-w-[340px]"
            role="search"
          >
            <i className="ri-search-line text-sm text-foreground-400" aria-hidden="true" />
            <input
              type="search"
              value={term}
              onChange={(event) => setTerm(event.target.value)}
              placeholder="搜索视频标题、简介或标签"
              aria-label="搜索视频"
              className="w-full bg-transparent text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none"
            />
            <button
              type="submit"
              className="focus-ring shrink-0 cursor-pointer rounded-full bg-primary-500 px-3 py-1 text-[11px] font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              搜索
            </button>
          </form>

          <button
            type="button"
            onClick={() => setOpen(true)}
            className="focus-ring ml-auto inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border border-background-300/80 bg-white text-foreground-800 md:hidden"
            aria-label="打开导航菜单"
            aria-expanded={open}
          >
            <i className="ri-menu-line text-lg" aria-hidden="true" />
          </button>
        </div>
      </div>

      {open ? (
        <div className="md:hidden">
          <button
            type="button"
            aria-label="关闭导航菜单"
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-[55] cursor-pointer bg-primary-950/50"
          />
          <div className="fixed inset-y-0 right-0 z-[60] flex w-[84%] max-w-xs animate-slide-in flex-col bg-background-50">
            <div className="flex items-center justify-between border-b border-background-200 px-4 py-4">
              <span className="font-heading text-base font-semibold text-foreground-950">
                色色视频
              </span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="focus-ring inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-200 text-foreground-700"
                aria-label="关闭菜单"
              >
                <i className="ri-close-line text-lg" aria-hidden="true" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-4">
              <form
                onSubmit={handleSubmit}
                className="mb-4 flex items-center gap-2 rounded-md border border-background-300/80 bg-white px-3 py-2"
                role="search"
              >
                <i className="ri-search-line text-sm text-foreground-400" aria-hidden="true" />
                <input
                  type="search"
                  value={term}
                  onChange={(event) => setTerm(event.target.value)}
                  placeholder="搜索视频标题或简介"
                  aria-label="搜索视频"
                  className="w-full bg-transparent text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none"
                />
              </form>

              <nav className="flex flex-col gap-1" aria-label="移动端导航">
                {navItems.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) =>
                      `flex items-center gap-3 rounded-md px-3 py-3 text-sm font-medium transition-colors ${
                        isActive
                          ? "bg-primary-50 text-primary-700"
                          : "text-foreground-700 hover:bg-background-100"
                      }`
                    }
                  >
                    <i className={`${item.icon} text-base`} aria-hidden="true" />
                    {item.label}
                    <i className="ri-arrow-right-s-line ml-auto text-base text-foreground-300" aria-hidden="true" />
                  </NavLink>
                ))}
              </nav>

              <div className="mt-5 rounded-md bg-background-100 px-3 py-3">
                <p className="text-[11px] leading-relaxed text-foreground-500">
                  本站为视频内容聚合展示平台，提供分类浏览与信息检索，不提供播放与下载服务。
                </p>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </header>
  );
}